import React, {
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import { usePopper } from 'react-popper';
import { Placement } from '@popperjs/core';
import { minorScale } from '.';
import { Portal } from './Portal';
import { CSSTransition } from 'react-transition-group';

import popoverStyles from './Popover.module.scss';

export interface IPopoverProps {
  content: (close: () => void) => ReactNode;
  children: ReactNode;
  placement?: Placement;
  offset?: number;
  onKeyDown?: (event: KeyboardEvent) => void;
  isOpen?: boolean;
  onOpen?: () => void;
  onClose?: () => void;
  tabIndex?: number;
  disabled?: boolean;
  canSelfClose?: boolean;
  triggerOnHover?: boolean;
  className?: string;
}

export const Popover: React.FC<IPopoverProps> = ({
  content,
  children,
  placement = 'bottom-start',
  offset = minorScale(1),
  onKeyDown,
  isOpen: defaultIsOpen,
  onOpen,
  onClose,
  tabIndex,
  disabled = false,
  canSelfClose = true,
  triggerOnHover = false,
  className,
}) => {
  const [isOpen, setIsOpen] = useState(defaultIsOpen);
  const [entered, setEntered] = useState(false);
  const [exitting, setExitting] = useState(false);

  useEffect(() => {
    if (exitting) return;
    setIsOpen(defaultIsOpen);
  }, [defaultIsOpen, exitting]);

  const [targetElem, setTargetElem] = useState<HTMLDivElement | null>(null);
  // We need both a ref object and callback as the CSSTransition removes the dom node
  // but popper does not register this - and then displays popperelem the wrong place.
  const [popperElem, setPopperElem] = useState<HTMLDivElement | null>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  const { styles, attributes } = usePopper(targetElem, popperElem, {
    placement,
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [0, offset],
        },
      },
    ],
  });

  const toogle = () => {
    if (isOpen) {
      close();
    } else if (!exitting) {
      open();
    }
  };

  const open = useCallback(() => {
    setIsOpen(true);
    onOpen && onOpen();
  }, [onOpen]);

  const close = useCallback(() => {
    if (canSelfClose) setIsOpen(false);
  }, [canSelfClose]);

  const handleBodyClick = useCallback(
    (event) => {
      // Ignore clicks on the popover or button
      if (targetElem && targetElem.contains(event.target)) {
        return;
      }
      if (popperElem && popperElem.contains(event.target)) {
        return;
      }
      close();
    },
    [close, targetElem, popperElem]
  );

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      // TODO: Is this necesarry?
      /*if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
      }*/
      if (event.key === 'Escape') {
        close();
      } else {
        onKeyDown && onKeyDown(event);
      }
    },
    [close, onKeyDown]
  );

  useEffect(() => {
    if (entered) {
      document.body.addEventListener('click', handleBodyClick, false);
      document.addEventListener('keydown', handleKeyDown, false);
    } else {
      document.body.removeEventListener('click', handleBodyClick, false);
      document.removeEventListener('keydown', handleKeyDown, false);
    }

    return () => {
      document.body.removeEventListener('click', handleBodyClick, false);
      document.removeEventListener('keydown', handleKeyDown, false);
    };
  }, [entered, handleBodyClick, handleKeyDown]);

  const [focussed, setFocussed] = useState(false);

  return (
    <>
      <div
        ref={setTargetElem}
        onMouseEnter={() => {
          triggerOnHover && open();
        }}
        onMouseLeave={() => {
          triggerOnHover && close();
        }}
        onClick={() => {
          if (!focussed || (focussed && !isOpen)) {
            !disabled && toogle();
          }
        }}
        onFocus={(e) => {
          setFocussed(true);
          e.stopPropagation();
          !disabled && toogle();
        }}
        onKeyDown={(e) => e.key === 'Enter' && toogle()}
        tabIndex={tabIndex}
        className={className}
      >
        {children}
      </div>
      <Portal>
        <CSSTransition
          unmountOnExit
          in={isOpen}
          timeout={100}
          onEntered={() => setEntered(true)}
          onExiting={() => setExitting(true)}
          onExited={() => {
            setEntered(false);
            setExitting(false);
            onClose && onClose();
          }}
          classNames={{
            enter: popoverStyles.enter,
            enterActive: popoverStyles.enterActive,
            exit: popoverStyles.exit,
            exitActive: popoverStyles.exitActive,
          }}
          nodeRef={popperRef}
        >
          <div
            ref={(r) => {
              setPopperElem(r);
              (
                popperRef as React.MutableRefObject<HTMLDivElement | null>
              ).current = r;
            }}
            className={popoverStyles.content}
            style={{ ...styles.popper, zIndex: 9999 }}
            {...attributes.popper}
          >
            {content(close)}
          </div>
        </CSSTransition>
      </Portal>
    </>
  );
};
